"use client"

// Right-column result card for the Lawn Lime Calculator. Renders either the
// "already in range" success state or the amendment (lime / sulfur) breakdown.
// Layout/copy mirror the approved design mockup; all numbers come from the
// lib/lime-calculator result. Product picks + email capture are rendered by the
// island below this card, so it stays focused on the calculation.

import { useState } from "react"
import { ArrowUp, ArrowDown, Divide, Info, Check, CheckCircle2, FileSearch } from "lucide-react"
import type { LimeCalculatorResult } from "@/lib/lime-calculator/calculator"

type Unit = "lbs" | "bags"

export function LimeResultCard({ result }: { result: LimeCalculatorResult }) {
  const [unit, setUnit] = useState<Unit>("lbs")

  if (result.inRange) {
    return (
      <div className="rounded-3xl bg-white border border-green-100 shadow-[0_12px_40px_rgba(0,0,0,0.06)] p-7 text-center">
        <div className="inline-flex items-center justify-center w-[60px] h-[60px] rounded-full bg-green-50 border border-green-200">
          <CheckCircle2 className="w-[30px] h-[30px] text-green-600" />
        </div>
        <h3 className="mt-4 text-2xl font-extrabold tracking-tight text-gray-900">
          Your pH is already right
        </h3>
        <p className="mt-2.5 text-[14.5px] leading-relaxed text-gray-700">{result.inRangeText}</p>
        <p className="mt-3.5 text-[13px] text-gray-500">
          Re-test every 2-3 years, or after heavy rain seasons on sandy soil.
        </p>
      </div>
    )
  }

  const isSulfur = result.direction === "sulfur"
  const totalLbs = result.totalLbs ?? 0
  const bags = result.bags ?? 1
  const bagLbs = result.bagLbs ?? 0

  const amountBig =
    unit === "lbs"
      ? `${totalLbs.toLocaleString()} lbs`
      : `${bags} ${bags === 1 ? "bag" : "bags"}`
  const amountSub =
    unit === "lbs"
      ? `of ${result.amendment?.toLowerCase()} for your lawn`
      : `of ${bagLbs} lb bags (${totalLbs.toLocaleString()} lbs total)`

  return (
    <div className="rounded-3xl overflow-hidden bg-white border border-green-100 shadow-[0_12px_40px_rgba(0,0,0,0.06)]">
      {/* colored header */}
      <div
        className={`flex items-center justify-between gap-3 px-6 py-4 text-white ${
          isSulfur
            ? "bg-linear-to-r from-amber-600 to-amber-700"
            : "bg-linear-to-r from-green-600 to-emerald-600"
        }`}
      >
        <span className="flex items-center gap-2.5 text-base font-bold">
          {isSulfur ? <ArrowDown className="w-[19px] h-[19px]" /> : <ArrowUp className="w-[19px] h-[19px]" />}
          {isSulfur ? "Lower your pH with sulfur" : "Raise your pH with lime"}
        </span>
        <span className="text-[13px] font-semibold opacity-90 whitespace-nowrap">
          pH {result.currentPh.toFixed(1)} → {result.targetPh.toFixed(1)}
        </span>
      </div>

      <div className="p-6">
        {/* amount hero */}
        <div className="p-5 rounded-[18px] bg-green-50 border border-green-200 text-center">
          <div className="text-[44px] font-extrabold leading-none tracking-tight text-green-900">
            {amountBig}
          </div>
          <div className="mt-2 text-sm font-semibold text-green-800">{amountSub}</div>
        </div>

        {/* lbs / bags toggle */}
        <div className="grid grid-cols-2 gap-1.5 mt-3 p-1.5 rounded-xl bg-gray-100" role="tablist" aria-label="Show amount in pounds or bags">
          {(["lbs", "bags"] as Unit[]).map((u) => (
            <button
              key={u}
              type="button"
              role="tab"
              aria-selected={unit === u}
              onClick={() => setUnit(u)}
              className={`min-h-[38px] px-2 rounded-[9px] cursor-pointer text-[13px] font-bold transition-colors ${
                unit === u ? "bg-white text-green-800 shadow-sm" : "bg-transparent text-gray-500"
              }`}
            >
              {u === "lbs" ? "Pounds" : `${bagLbs} lb bags`}
            </button>
          ))}
        </div>

        {/* stat tiles */}
        <div className="grid grid-cols-2 gap-3 mt-3.5">
          <div className="p-3.5 rounded-2xl bg-gray-50 border border-gray-200 text-center">
            <div className="text-[22px] font-extrabold leading-none text-gray-900">
              {result.lbsPer1000} lbs
            </div>
            <div className="mt-1.5 text-xs font-semibold text-gray-500">per 1,000 sq ft, total</div>
          </div>
          <div className="p-3.5 rounded-2xl bg-gray-50 border border-gray-200 text-center">
            <div className="text-[22px] font-extrabold leading-none text-gray-900">
              {result.applications}
            </div>
            <div className="mt-1.5 text-xs font-semibold text-gray-500">
              {result.applications === 1 ? "application needed" : "applications, months apart"}
            </div>
          </div>
        </div>

        {/* split note */}
        {result.splitNote && (
          <div className="flex items-start gap-2.5 mt-3.5 px-3.5 py-3 rounded-[13px] bg-amber-50 border border-amber-200">
            <Divide className="shrink-0 w-4 h-4 mt-0.5 text-amber-600" />
            <p className="text-[13px] leading-snug text-amber-800">{result.splitNote}</p>
          </div>
        )}

        {/* source reason */}
        {result.sourceReason && (
          <div className="flex items-start gap-2.5 mt-3.5 px-3.5 py-3 rounded-[13px] bg-green-50 border border-green-200">
            <Info className="shrink-0 w-4 h-4 mt-0.5 text-green-600" />
            <p className="text-[13px] leading-snug text-green-800">{result.sourceReason}</p>
          </div>
        )}

        {/* timing + safety */}
        <p className="mt-5 mb-2.5 text-[13px] font-bold uppercase tracking-[0.05em] text-gray-500">
          Timing &amp; safety
        </p>
        <div className="flex flex-col gap-2">
          {result.timingNotes.map((note, i) => (
            <div key={i} className="flex items-start gap-2.5">
              <Check className="shrink-0 w-[15px] h-[15px] mt-0.5 text-green-600" />
              <span className="text-[13.5px] leading-normal text-gray-700">{note}</span>
            </div>
          ))}
        </div>

        {/* analyzer cross-link */}
        <a
          href="/tools/soil-test-analyzer"
          className="flex items-center gap-3 mt-4 px-3.5 py-3 rounded-[13px] bg-gray-50 border border-gray-200 no-underline"
        >
          <FileSearch className="shrink-0 w-[18px] h-[18px] text-green-600" />
          <p className="m-0 flex-1 text-[13px] leading-normal text-gray-700">
            <strong className="text-gray-900">Have a full lab soil test?</strong> The Soil Test Analyzer
            reads the whole report: pH, nutrients, and CEC.
          </p>
          <span className="text-[13px] font-bold text-green-600 whitespace-nowrap">Run it →</span>
        </a>
      </div>
    </div>
  )
}
